import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PAYMENT_METHODS } from '@makila/fiscal';
import { Banknote, FilePlus2, MoreHorizontal, Pencil, Trash2, Truck } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import {
  Badge, Button, Card, Dropdown, Empty, Field, Input, KeyValues, Loading, Modal, PageHeader, Pagination, SearchInput, Select, StatCard, StatusBadge, SuggestInput, Switch, Table, Tabs, Td, Textarea, Th, Toolbar, Tr, errorText, fieldErrors, useConfirm, useToast,
} from '../components/ui';
import { del, get, patch, post } from '../lib/api';
import { useAuth } from '../lib/auth';
import { date, mt } from '../lib/format';
import { BANKS, PROVINCES } from '../lib/lists';
import { SupplierPaymentModal } from './purchases';

type S = Record<string, any>;
const v = (x: unknown) => (x === null || x === undefined ? '' : String(x));
type FormTab = 'geral' | 'contactos' | 'pagamento' | 'notas';

export function SupplierForm({ initial, onSaved, onCancel }: { initial?: S; onSaved: (s: S) => void; onCancel: () => void }) {
  const toast = useToast();
  const [tab, setTab] = useState<FormTab>('geral');
  const [f, setF] = useState({
    code: v(initial?.code), name: v(initial?.name), nuit: v(initial?.nuit), category: v(initial?.category), address: v(initial?.address), city: v(initial?.city),
    province: v(initial?.province), postalCode: v(initial?.postal_code), email: v(initial?.email), phone: v(initial?.phone), mobile: v(initial?.mobile), website: v(initial?.website),
    contactName: v(initial?.contact_name), paymentTermsDays: v(initial?.payment_terms_days), defaultPaymentMethod: v(initial?.default_payment_method),
    bankName: v(initial?.bank_name), bankAccount: v(initial?.bank_account), nib: v(initial?.nib), notes: v(initial?.notes), active: initial?.active ?? true,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setErrors({});
    const body = { ...f, code: f.code || null, nuit: f.nuit || null, paymentTermsDays: f.paymentTermsDays === '' ? null : f.paymentTermsDays, defaultPaymentMethod: f.defaultPaymentMethod || null };
    try {
      const r = initial ? await patch(`/suppliers/${initial.id}`, body) : await post('/suppliers', body);
      toast(initial ? 'Fornecedor actualizado' : 'Fornecedor criado');
      onSaved(r.supplier);
    } catch (ex) {
      const fe = fieldErrors(ex); setErrors(fe);
      if (fe.nib) setTab('pagamento'); else if (fe.email) setTab('contactos'); else if (fe.name || fe.nuit) setTab('geral');
      toast(errorText(ex), 'error');
    } finally { setBusy(false); }
  }
  return (
    <form onSubmit={submit} noValidate>
      <Tabs value={tab} onChange={setTab} className="-mx-6 -mt-5 mb-5 px-6" tabs={[{ id: 'geral', label: 'Identificação' }, { id: 'contactos', label: 'Contactos e morada' }, { id: 'pagamento', label: 'Pagamento e banco' }, { id: 'notas', label: 'Notas' }]} />
      <div className="min-h-[260px]">
        {tab === 'geral' && (
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="Nome / firma" required className="sm:col-span-2" error={errors.name}><Input autoFocus value={f.name} onChange={set('name')} /></Field>
            <Field label="NUIT" error={errors.nuit}><Input inputMode="numeric" value={f.nuit} onChange={set('nuit')} /></Field>
            <Field label="Código" hint="Vazio = automático"><Input value={f.code} onChange={set('code')} /></Field>
            <Field label="Categoria" className="sm:col-span-2" hint="Ex.: mercadorias, serviços, combustível"><Input value={f.category} onChange={set('category')} /></Field>
            {initial && <div className="sm:col-span-3"><Switch label="Fornecedor activo" checked={f.active} onChange={(x) => setF({ ...f, active: x })} /></div>}
          </div>
        )}
        {tab === 'contactos' && (
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Email" error={errors.email}><Input type="email" value={f.email} onChange={set('email')} /></Field>
            <Field label="Website"><Input value={f.website} onChange={set('website')} /></Field>
            <Field label="Telefone"><Input type="tel" value={f.phone} onChange={set('phone')} /></Field>
            <Field label="Telemóvel"><Input type="tel" value={f.mobile} onChange={set('mobile')} /></Field>
            <Field label="Pessoa de contacto" className="sm:col-span-2"><Input value={f.contactName} onChange={set('contactName')} /></Field>
            <Field label="Morada" className="sm:col-span-2"><Input value={f.address} onChange={set('address')} /></Field>
            <Field label="Cidade"><Input value={f.city} onChange={set('city')} /></Field>
            <Field label="Província"><SuggestInput suggestions={PROVINCES} value={f.province} onChange={set('province')} /></Field>
          </div>
        )}
        {tab === 'pagamento' && (
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Prazo de pagamento (dias)" hint="Calcula o vencimento das facturas"><Input inputMode="numeric" value={f.paymentTermsDays} onChange={set('paymentTermsDays')} /></Field>
            <Field label="Meio de pagamento habitual">
              <Select value={f.defaultPaymentMethod} onChange={set('defaultPaymentMethod')}><option value="">—</option>{Object.entries(PAYMENT_METHODS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</Select>
            </Field>
            <Field label="Banco"><SuggestInput suggestions={BANKS} value={f.bankName} onChange={set('bankName')} placeholder="BCI, Millennium BIM, Standard Bank…" /></Field>
            <Field label="N.º de conta"><Input value={f.bankAccount} onChange={set('bankAccount')} /></Field>
            <Field label="NIB" className="sm:col-span-2" error={errors.nib} hint="21 dígitos"><Input inputMode="numeric" value={f.nib} onChange={set('nib')} /></Field>
          </div>
        )}
        {tab === 'notas' && <Field label="Notas internas"><Textarea rows={7} value={f.notes} onChange={set('notes')} /></Field>}
      </div>
      <div className="-mx-6 mt-5 -mb-5 flex justify-end gap-2 border-t border-[var(--border)] bg-[var(--surface-2)] px-6 py-3.5">
        <Button variant="secondary" onClick={onCancel}>Cancelar</Button>
        <Button type="submit" loading={busy}>{initial ? 'Guardar alterações' : 'Criar fornecedor'}</Button>
      </div>
    </form>
  );
}

export function SuppliersPage() {
  const { can } = useAuth();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [sp, setSp] = useSearchParams();
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const creating = sp.get('novo') === '1';
  const setCreating = (x: boolean) => { const p = new URLSearchParams(sp); if (x) p.set('novo', '1'); else p.delete('novo'); setSp(p, { replace: true }); };
  const list = useQuery({ queryKey: ['suppliers', q, page], queryFn: () => get<{ items: S[]; total: number }>(`/suppliers?page=${page}&pageSize=25&q=${encodeURIComponent(q)}`) });
  return (
    <>
      <PageHeader eyebrow="Compras" title="Fornecedores" actions={can('purchases:create') && <Button icon={<Truck className="size-4" />} onClick={() => setCreating(true)}>Novo fornecedor</Button>} />
      <Card padded={false}>
        <Toolbar><SearchInput className="w-72" value={q} onChange={(x) => { setQ(x); setPage(1); }} placeholder="Nome, NUIT ou código" /></Toolbar>
        {list.isLoading ? <div className="p-5"><Loading /></div> : !list.data?.items.length ? (
          <Empty icon={<Truck />} title="Sem fornecedores" action={can('purchases:create') && <Button onClick={() => setCreating(true)}>Criar fornecedor</Button>}>Registe quem lhe vende para controlar compras e contas a pagar.</Empty>
        ) : (
          <>
            <Table>
              <thead><tr><Th>Fornecedor</Th><Th>NUIT</Th><Th>Categoria</Th><Th>Contacto</Th><Th className="text-right">Em dívida</Th></tr></thead>
              <tbody>{list.data.items.map((s) => (
                <Tr key={s.id} onClick={() => nav(`/fornecedores/${s.id}`)}>
                  <Td><div className="font-medium">{s.name} {!s.active && <Badge tone="red">Inactivo</Badge>}</div><div className="text-xs text-[var(--muted)]">{s.code}{s.city ? ` · ${s.city}` : ''}</div></Td>
                  <Td className="tabular-nums">{s.nuit ?? '—'}</Td><Td>{s.category ?? '—'}</Td>
                  <Td className="text-[var(--text-2)]">{s.contact_name ?? ''}<div className="text-xs text-[var(--muted)]">{s.mobile ?? s.phone ?? s.email ?? ''}</div></Td>
                  <Td className={`num font-medium ${Number(s.payable) > 0 ? 'text-amber-700 dark:text-amber-400' : 'text-[var(--muted)]'}`}>{mt(s.payable)}</Td>
                </Tr>
              ))}</tbody>
            </Table>
            <Pagination page={page} pageSize={25} total={list.data.total} onPage={setPage} />
          </>
        )}
      </Card>
      <Modal open={creating} onClose={() => setCreating(false)} title="Novo fornecedor" size="lg" icon={<Truck />}>
        <SupplierForm onCancel={() => setCreating(false)} onSaved={(s) => { setCreating(false); qc.invalidateQueries({ queryKey: ['suppliers'] }); nav(`/fornecedores/${s.id}`); }} />
      </Modal>
    </>
  );
}

export function SupplierPage() {
  const { id } = useParams();
  const { can } = useAuth();
  const nav = useNavigate();
  const toast = useToast();
  const confirm = useConfirm();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [paying, setPaying] = useState(false);
  const r = useQuery({ queryKey: ['supplier', id], queryFn: () => get(`/suppliers/${id}`) });
  if (r.isLoading || !r.data) return <Loading />;
  const s = r.data.supplier;
  const refresh = () => { qc.invalidateQueries({ queryKey: ['supplier', id] }); qc.invalidateQueries({ queryKey: ['suppliers'] }); };
  const remove = async () => {
    if (!(await confirm({ title: `Remover ${s.name}?`, description: 'Se tiver compras registadas, fica apenas inactivo.', tone: 'danger', confirmLabel: 'Remover' }))) return;
    try { const x = await del(`/suppliers/${id}`); toast(x.deactivated ? 'Fornecedor desactivado' : 'Fornecedor removido'); refresh(); nav('/fornecedores'); } catch (e) { toast(errorText(e), 'error'); }
  };
  return (
    <>
      <PageHeader eyebrow={<Link to="/fornecedores" className="hover:underline">Fornecedores</Link>} title={s.name} subtitle={[s.code, s.nuit && `NUIT ${s.nuit}`, s.city].filter(Boolean).join(' · ')}
        actions={<>
          {can('purchases:create') && <Link to={`/compras/nova?fornecedor=${id}`}><Button icon={<FilePlus2 className="size-4" />}>Registar factura</Button></Link>}
          {can('purchases:create') && Number(r.data.payable) > 0 && <Button variant="secondary" icon={<Banknote className="size-4" />} onClick={() => setPaying(true)}>Pagar</Button>}
          <Dropdown trigger={<Button variant="secondary" iconOnly icon={<MoreHorizontal className="size-4" />} aria-label="Mais acções" />} items={[
            { label: 'Editar ficha', icon: <Pencil />, onClick: () => setEditing(true), hidden: !can('purchases:edit') },
            'sep',
            { label: 'Remover fornecedor', icon: <Trash2 />, danger: true, onClick: remove, hidden: !can('purchases:delete') },
          ]} />
        </>} />
      <div className="grid gap-4 sm:grid-cols-3">
        <StatCard label="Em dívida" value={`${mt(r.data.payable)} MT`} tone={Number(r.data.payable) > 0 ? undefined : 'good'} icon={<Banknote />} />
        <StatCard label="Facturas registadas" value={r.data.purchases.length} />
        <StatCard label="Prazo de pagamento" value={s.payment_terms_days != null ? `${s.payment_terms_days} dias` : '—'} />
      </div>
      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        <Card title="Dados" className="lg:col-span-1">
          <KeyValues cols={1} items={[
            ['Categoria', s.category], ['Contacto', s.contact_name], ['Email', s.email], ['Telefone', s.phone ?? s.mobile],
            ['Morada', [s.address, s.city, s.province].filter(Boolean).join(', ')], ['Banco', [s.bank_name, s.bank_account].filter(Boolean).join(' · ')], ['NIB', s.nib],
            ['Meio habitual', s.default_payment_method && PAYMENT_METHODS[s.default_payment_method as keyof typeof PAYMENT_METHODS]], ['Notas', s.notes],
          ]} />
        </Card>
        <Card title="Facturas do fornecedor" className="lg:col-span-2" padded={false}>
          {!r.data.purchases.length ? <Empty title="Sem facturas" /> : (
            <Table>
              <thead><tr><Th>N.º do fornecedor</Th><Th>Data</Th><Th>Vencimento</Th><Th>Estado</Th><Th className="text-right">Total</Th><Th className="text-right">Em dívida</Th></tr></thead>
              <tbody>{r.data.purchases.map((p: any) => (
                <Tr key={p.id} onClick={() => nav(`/compras/${p.id}`)}>
                  <Td className="font-medium">{p.supplier_doc_number}</Td><Td>{date(p.doc_date)}</Td><Td>{date(p.due_date)}</Td><Td><StatusBadge status={p.status} /></Td>
                  <Td className="num">{mt(p.gross_total)}</Td><Td className="num">{p.status === 'posted' ? mt(p.pending) : '—'}</Td>
                </Tr>
              ))}</tbody>
            </Table>
          )}
        </Card>
      </div>
      <Modal open={editing} onClose={() => setEditing(false)} title="Editar fornecedor" size="lg" icon={<Pencil />}>
        <SupplierForm initial={s} onCancel={() => setEditing(false)} onSaved={() => { setEditing(false); refresh(); }} />
      </Modal>
      <SupplierPaymentModal open={paying} onClose={() => setPaying(false)} supplier={{ id: s.id, name: s.name, code: s.code, nuit: s.nuit, city: s.city, payment_terms_days: s.payment_terms_days, default_payment_method: s.default_payment_method }} onSaved={refresh} />
    </>
  );
}
